<template>
  <v-navigation-drawer
    v-model="isVisible"
    right
    width="560"
    fixed
    temporary
    class="sip-detail-drawer">
    <div class="drawer-content">
      <div class="drawer-header">
        <div class="header-content">
          <h2 class="drawer-title ds-panel-title">
            {{ drawerTitle }}
          </h2>
          <v-btn icon class="close-btn" @click="onClose">
            <v-icon size="20">
              mdi-close
            </v-icon>
          </v-btn>
        </div>
      </div>

      <div class="drawer-body">
        <div v-if="loading" class="py-12 text-center">
          <v-progress-circular color="primary" indeterminate size="40"/>
          <p class="text-body-3 text--secondary mt-4">
            Loading…
          </p>
        </div>

        <v-alert v-else-if="fetchError" type="error" dense outlined class="mb-0">
          {{ fetchError }}
        </v-alert>

        <template v-else-if="kind === 'trunk'">
          <p class="text-body-3 text--secondary drawer-id-chip mb-6">
            ID · <span class="text-element-2 text-semibold">{{ resourceId }}</span>
          </p>

          <div class="form-group">
            <label class="input-label">
              Trunk name <span class="required">*</span>
            </label>
            <MankTextField
              v-model="trunkForm.name"
              dense
              hide-details="auto"
              placeholder="e.g. main-pbx"/>
          </div>
          <div class="form-group">
            <label class="input-label">
              SIP username <span class="required">*</span>
            </label>
            <MankTextField
              v-model="trunkForm.auth_username"
              dense
              hide-details="auto"
              autocomplete="username"
              placeholder="SIP username"/>
          </div>
          <div class="form-group">
            <label class="input-label">
              SIP password (new)
            </label>
            <MankTextField
              v-model="trunkForm.auth_password"
              type="password"
              dense
              hide-details="auto"
              autocomplete="new-password"
              placeholder="Leave blank to keep current password"/>
            <p class="field-hint text-body-3 text--secondary">
              Optional. Only sends a credential update when filled.
            </p>
          </div>
          <div class="form-group">
            <label class="input-label">
              Metadata
            </label>
            <MankTextField
              v-model="trunkForm.metadata"
              dense
              hide-details="auto"/>
          </div>
          <div class="form-group">
            <label class="input-label">
              Phone numbers
            </label>
            <MankTextarea
              v-model="trunkForm.numbersRaw"
              rows="2"
              dense
              hide-details="auto"
              placeholder="Comma or newline"/>
          </div>
          <div class="form-group">
            <label class="input-label">
              Allowed addresses
            </label>
            <MankTextarea
              v-model="trunkForm.allowedAddressesRaw"
              rows="2"
              dense
              hide-details="auto"
              placeholder="IPs or ranges"/>
          </div>
          <div class="form-group">
            <label class="input-label">
              Allowed numbers (CLI)
            </label>
            <MankTextarea
              v-model="trunkForm.allowedNumbersRaw"
              rows="2"
              dense
              hide-details="auto"/>
          </div>
        </template>

        <template v-else-if="kind === 'dispatch'">
          <p class="text-body-3 text--secondary drawer-id-chip mb-4">
            ID · <span class="text-element-2 text-semibold">{{ resourceId }}</span>
          </p>

          <div v-if="dispatchHasNonDirectVariants" class="mb-6">
            <v-alert dense outlined type="info">
              This rule also defines callee / individual routing branches. Editing here updates the replaced document — double-check unchanged branches in LiveKit if needed.
            </v-alert>
          </div>

          <div class="form-group">
            <label class="input-label">
              Bound trunk IDs
            </label>
            <MankTextField
              :value="dispatchTrunkIdsDisplay"
              readonly
              dense
              hide-details="auto"/>
          </div>
          <div class="form-group">
            <label class="input-label">
              Rule display name (optional)
            </label>
            <MankTextField
              v-model="dispatchForm.name"
              dense
              hide-details="auto"/>
          </div>
          <div class="form-group">
            <label class="input-label">
              Metadata (optional)
            </label>
            <MankTextField
              v-model="dispatchForm.metadata"
              dense
              hide-details="auto"/>
          </div>
          <div class="form-group">
            <label class="input-label">
              Room name <span class="required">*</span>
            </label>
            <MankTextField
              v-model="dispatchForm.roomName"
              dense
              hide-details="auto"
              placeholder="LiveKit room"/>
          </div>
          <div class="form-group">
            <label class="input-label">
              PIN (optional)
            </label>
            <MankTextField
              v-model="dispatchForm.pin"
              dense
              hide-details="auto"/>
          </div>
          <div class="form-group">
            <label class="input-label">
              Agent name
            </label>
            <MankTextField
              v-model="dispatchForm.agentName"
              dense
              hide-details="auto"
              placeholder="base-agent"/>
          </div>
        </template>
      </div>

      <div v-if="!loading && !fetchError && resourceId" class="drawer-footer">
        <div class="footer-buttons">
          <v-btn elevation="0" class="cancel-btn mank-btn" @click="onCancel">
            <Icon name="cross" variant="solid" color="default" size="20"/>
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            elevation="0"
            class="apply-btn mank-btn"
            :loading="saving"
            @click="onSave">
            <Icon name="check" variant="solid" color="onPrimary" size="20"/>
            Save changes
          </v-btn>
        </div>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import Icon from '@/components/Icon.vue';
import LivekitService from '@/services/LivekitService';
import MankTextarea from '@/components/MankTextarea.vue';
import MankTextField from '@/components/MankTextField.vue';

function deepCloneJson(obj) {
  if (obj === undefined) return undefined;
  return JSON.parse(JSON.stringify(obj));
}

function arrEqual(a, b) {
  return JSON.stringify(a || []) === JSON.stringify(b || []);
}

export default {
  name: 'SipTrunkDetailDrawer',
  components: {
    Icon,
    MankTextarea,
    MankTextField,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    kind: {
      type: String,
      default: 'trunk',
      validator: (v) => v === 'trunk' || v === 'dispatch',
    },
    resourceId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loading: false,
      saving: false,
      fetchError: '',
      trunkBaseline: {},
      trunkForm: {
        name: '',
        auth_username: '',
        auth_password: '',
        metadata: '',
        numbersRaw: '',
        allowedAddressesRaw: '',
        allowedNumbersRaw: '',
      },
      dispatchBaseline: {},
      dispatchForm: {
        name: '',
        metadata: '',
        roomName: '',
        pin: '',
        agentName: 'base-agent',
      },
    };
  },
  computed: {
    isVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        if (!value) this.$emit('close');
      },
    },
    drawerTitle() {
      if (!this.resourceId) return '';
      if (this.kind === 'dispatch') return 'Dispatch rule · detail & update';
      return 'Inbound SIP trunk · detail & update';
    },
    dispatchTrunkIdsDisplay() {
      const ids = this.dispatchBaseline && Array.isArray(this.dispatchBaseline.trunk_ids)
        ? this.dispatchBaseline.trunk_ids
        : [];
      return ids.length ? ids.join(', ') : '—';
    },
    dispatchHasNonDirectVariants() {
      const r = this.dispatchBaseline && this.dispatchBaseline.rule;
      if (!r) return false;
      return !!(r.dispatch_rule_callee || r.dispatch_rule_individual
        || r.dispatchRuleCallee || r.dispatchRuleIndividual);
    },
  },
  watch: {
    visible(next) {
      if (next && this.resourceId) {
        this.loadDetail();
      }
    },
    resourceId() {
      if (this.visible && this.resourceId) {
        this.loadDetail();
      }
    },
  },
  methods: {
    unwrapApiPayload(payload) {
      if (payload == null) return null;
      if (typeof payload === 'object' && payload.success === false) return null;
      if (payload && typeof payload === 'object' && Object.prototype.hasOwnProperty.call(payload, 'data')) {
        const d = payload.data;
        return d !== undefined && d !== null ? d : null;
      }
      return payload;
    },
    normalizeTrunkBaseline(raw) {
      const payload = this.unwrapApiPayload(raw);
      if (!payload) return null;
      let t = payload.trunk;
      if (!t || typeof t !== 'object') {
        const alt = payload.sipInboundTrunk || payload.sip_inbound_trunk || payload.inbound_trunk || payload.Trunk;
        t = typeof alt === 'object' ? alt : payload;
      }
      if (!t || typeof t !== 'object') return null;
      return {
        name: t.name ?? '',
        auth_username: t.auth_username ?? t.authUsername ?? '',
        metadata: t.metadata ?? '',
        numbers: Array.isArray(t.numbers) ? [...t.numbers] : [],
        allowed_addresses: Array.isArray(t.allowed_addresses)
          ? [...t.allowed_addresses]
          : Array.isArray(t.allowedAddresses) ? [...t.allowedAddresses] : [],
        allowed_numbers: Array.isArray(t.allowed_numbers)
          ? [...t.allowed_numbers]
          : Array.isArray(t.allowedNumbers) ? [...t.allowedNumbers] : [],
      };
    },
    normalizeDispatchBaseline(raw) {
      const payload = this.unwrapApiPayload(raw);
      if (!payload || typeof payload !== 'object') return null;
      const baseline = {};
      baseline.name = payload.name ?? '';
      baseline.metadata = payload.metadata ?? '';
      baseline.trunk_ids = [...(payload.trunk_ids ?? payload.trunkIds ?? [])];
      baseline.rule = deepCloneJson(payload.rule) || {};
      baseline.room_config = deepCloneJson(payload.room_config ?? payload.roomConfig) || {};

      const r = baseline.rule;
      baseline.rule = r && typeof r === 'object' ? r : {};

      let direct = baseline.rule.dispatch_rule_direct;
      if (!direct && baseline.rule.dispatchRuleDirect) {
        direct = baseline.rule.dispatchRuleDirect;
        baseline.rule.dispatch_rule_direct = direct || {};
      } else if (!direct) {
        baseline.rule.dispatch_rule_direct = baseline.rule.dispatch_rule_direct || {};
        direct = baseline.rule.dispatch_rule_direct;
      }
      if (!direct || typeof direct !== 'object') {
        baseline.rule.dispatch_rule_direct = {};
      }

      const rc = baseline.room_config && typeof baseline.room_config === 'object'
        ? baseline.room_config : {};
      if (!Array.isArray(rc.agents) || rc.agents.length === 0) rc.agents = [{ agent_name: 'base-agent' }];
      baseline.room_config = rc;

      return baseline;
    },
    parseLines(raw) {
      if (!raw || !String(raw).trim()) return [];
      return String(raw).split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
    },
    populateTrunkForms(baseline) {
      if (!baseline) return;
      this.trunkBaseline = { ...baseline, numbers: [...(baseline.numbers || [])],
        allowed_addresses: [...(baseline.allowed_addresses || [])],
        allowed_numbers: [...(baseline.allowed_numbers || [])],
      };
      this.trunkForm = {
        name: baseline.name || '',
        auth_username: baseline.auth_username || '',
        auth_password: '',
        metadata: baseline.metadata || '',
        numbersRaw: (baseline.numbers || []).join(', '),
        allowedAddressesRaw: (baseline.allowed_addresses || []).join(', '),
        allowedNumbersRaw: (baseline.allowed_numbers || []).join(', '),
      };
    },
    populateDispatchForms(baseline) {
      const b = baseline;
      const direct = b.rule.dispatch_rule_direct || {};
      let agentName = 'base-agent';
      const agents = b.room_config && Array.isArray(b.room_config.agents) ? b.room_config.agents : [];
      const firstAgent = agents[0];
      if (firstAgent && firstAgent.agent_name) agentName = firstAgent.agent_name;
      else if (firstAgent && firstAgent.agentName) agentName = firstAgent.agentName;

      this.dispatchBaseline = deepCloneJson(baseline);
      this.dispatchForm = {
        name: b.name || '',
        metadata: b.metadata || '',
        roomName: direct.room_name ?? direct.roomName ?? '',
        pin: direct.pin ?? '',
        agentName,
      };
    },
    resetState() {
      this.fetchError = '';
      this.trunkBaseline = {};
      this.dispatchBaseline = {};
    },
    async loadDetail() {
      this.loading = true;
      this.fetchError = '';
      this.resetState();

      try {
        if (this.kind === 'trunk') {
          const raw = await LivekitService.getInboundTrunk(this.resourceId);
          const baseline = this.normalizeTrunkBaseline(raw);
          if (!baseline) {
            this.fetchError = 'Could not read trunk payload from API.';
          } else {
            this.populateTrunkForms(baseline);
          }
        } else {
          const raw = await LivekitService.getDispatchRule(this.resourceId);
          const baseline = this.normalizeDispatchBaseline(raw);
          if (!baseline) {
            this.fetchError = 'Could not read dispatch rule from API.';
          } else {
            this.populateDispatchForms(baseline);
          }
        }
      } catch (err) {
        this.fetchError = err.response?.data?.message || err.message || 'Request failed.';
      } finally {
        this.loading = false;
      }
    },
    buildTrunkUpdatePayload() {
      const snap = this.trunkBaseline;
      const update = {};
      const f = this.trunkForm;
      const name = (f.name || '').trim();
      const user = (f.auth_username || '').trim();
      if (name !== (snap.name || '')) update.name = name;
      if (user !== (snap.auth_username || '')) update.auth_username = user;

      const meta = (f.metadata || '').trim();
      if (meta !== (snap.metadata || '')) update.metadata = meta;

      const nums = this.parseLines(f.numbersRaw);
      if (!arrEqual(nums, snap.numbers)) update.numbers = { set: nums };

      const addrs = this.parseLines(f.allowedAddressesRaw);
      if (!arrEqual(addrs, snap.allowed_addresses)) update.allowed_addresses = { set: addrs };

      const allowNums = this.parseLines(f.allowedNumbersRaw);
      if (!arrEqual(allowNums, snap.allowed_numbers)) update.allowed_numbers = { set: allowNums };

      const pwd = (f.auth_password || '').trim();
      if (pwd) update.auth_password = pwd;

      if (Object.keys(update).length === 0) return null;
      return { update };
    },
    async saveTrunk() {
      if (!(this.trunkForm.name || '').trim() || !(this.trunkForm.auth_username || '').trim()) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          message: 'Trunk name and SIP username are required.',
          timeout: 4000,
        });
        return;
      }

      const body = this.buildTrunkUpdatePayload();
      if (!body) {
        this.$store.dispatch('ui/showNotification', {
          type: 'info',
          message: 'No changes to save.',
          timeout: 4000,
        });
        return;
      }
      this.saving = true;
      try {
        await LivekitService.patchInboundTrunk(this.resourceId, body);
        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          message: 'Trunk updated.',
          timeout: 5000,
        });
        this.$emit('saved');
        this.$emit('close');
      } catch (err) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          message: err.response?.data?.message || err.message || 'Update failed.',
          timeout: 5000,
        });
      } finally {
        this.saving = false;
      }
    },
    buildDispatchReplaceBody() {
      const next = deepCloneJson(this.dispatchBaseline);
      if (!next.rule) next.rule = {};
      if (!next.rule.dispatch_rule_direct) next.rule.dispatch_rule_direct = {};

      next.name = (this.dispatchForm.name || '').trim();
      next.metadata = (this.dispatchForm.metadata || '').trim();

      next.rule.dispatch_rule_direct.room_name = (this.dispatchForm.roomName || '').trim();
      const pin = (this.dispatchForm.pin || '').trim();
      if (pin) next.rule.dispatch_rule_direct.pin = pin;
      else delete next.rule.dispatch_rule_direct.pin;

      if (!next.room_config) next.room_config = {};
      next.room_config.agents = [{ agent_name: (this.dispatchForm.agentName || 'base-agent').trim() || 'base-agent' }];

      return { replace: next };
    },
    async saveDispatch() {
      if (!(this.dispatchForm.roomName || '').trim()) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          message: 'Room name is required.',
          timeout: 4000,
        });
        return;
      }
      this.saving = true;
      try {
        const body = this.buildDispatchReplaceBody();
        await LivekitService.patchDispatchRule(this.resourceId, body);
        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          message: 'Dispatch rule updated.',
          timeout: 5000,
        });
        this.$emit('saved');
        this.$emit('close');
      } catch (err) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          message: err.response?.data?.message || err.message || 'Update failed.',
          timeout: 5000,
        });
      } finally {
        this.saving = false;
      }
    },
    async onSave() {
      if (this.kind === 'trunk') await this.saveTrunk();
      else await this.saveDispatch();
    },
    onClose() {
      this.$emit('close');
    },
    onCancel() {
      this.$emit('close');
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/styles/colors.scss';
@import '@/styles/variables.scss';

.sip-detail-drawer {
  &:deep(.v-navigation-drawer__content) {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    background-color: $light-surface-2;
    border-left: 1px solid $light-border !important;
  }
}

.drawer-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.drawer-header {
  flex-shrink: 0;
  border-bottom: 1px solid $light-border;
  padding: $mank-space-4 $mank-space-6;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.drawer-title {
  margin: 0;
  font-size: $text-size-18 !important;
  line-height: 1.35;
}

.close-btn {
  flex-shrink: 0;
}

.drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px $mank-space-6;
}

.drawer-id-chip {
  margin: 0;
  word-break: break-all;
}

.form-group {
  margin-bottom: 18px;
}

.input-label {
  display: block;
  font-size: $text-size-13;
  font-weight: 600;
  margin-bottom: 8px;
  color: $light-foreground-1;
}

.required {
  color: $light-primary;
}

.field-hint {
  margin: 6px 0 0;
}

.drawer-footer {
  flex-shrink: 0;
  padding: $mank-space-4 $mank-space-6;
  border-top: 1px solid $light-border;
  background: $light-surface-1;
}

.footer-buttons {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 12px;
}

.cancel-btn,
.apply-btn {
  font-weight: 600;
}

.cancel-btn {
  color: $light-foreground-1;
  background-color: $light-surface-2 !important;
}
</style>
